import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ButtonAlignmentEnum, DynamicFormOptionDto, DynamicValueDataTypeEnum, WorkflowActionTypeEnum, WorkflowDto, WorkflowStepButtonActionDto, WorkflowStepButtonDto, WorkflowStepDto, WorkflowStepReadOnlyFieldDto, WorkflowStepSectionDto, WorkflowStepSectionElementDto, WorkflowStepStatusDto } from "../../../service/dto";
import { WorkflowFormComponent } from "./workflow-form.component";
import { NzButtonType } from "ng-zorro-antd/button";

function nonEmptyArrayValidator() {
	return (control: AbstractControl): { [key: string]: any } | null => {
		const value = control.value as number[];
		return value && value.length > 0 ? null : { required: true };
	};
}


export class WrapperWorkflowDto {
	payload!: WorkflowDto;
	form!: FormGroup;
	steps!: WrapperWorkflowStepDto[];

	get _name(): string | undefined {
		return this.form.get('name')?.value;
	}
	getAllElementsAtStep(input: WrapperWorkflowStepDto): { element: WrapperWorkflowElementDto, step: WrapperWorkflowStepDto }[] {
		const out: { element: WrapperWorkflowElementDto, step: WrapperWorkflowStepDto }[] = [];
		this.steps.filter(item => (item._stepOrder ?? 1000) <= (input._stepOrder ?? 1000)).forEach(step => {
			step.sections.forEach(section => {
				section.elements.forEach(element => {
					out.push({ element: element, step: step });
				});
			});
		});
		return out;
	}
	get postBody(): WorkflowDto {
		return {
			id: this.payload.id,
			createdAt: new Date(),
			name: this.form.get('name')!.value,
			label: this.form.get('label')!.value,
			description: this.form.get('description')!.value,
			steps: this.steps.map(item => item.postBody),
			availableReadOnlyFields: this.payload.availableReadOnlyFields,
			autoAssign: this.form.get('autoAssign')!.value,
			linkMemberships: this.form.get('linkMemberships')!.value,
			ticketMode: this.form.get('ticketMode')!.value,
			linkToOtherTicketEnabled: this.form.get('linkToOtherTicketEnabled')!.value,
			referencePrefix: this.form.get('referencePrefix')!.value,
			reports: [],
			reportColumnTemplates: [],
			hashCode: 0
		};
	}
}


export class WrapperWorkflowReadOnlyFieldDto {
	payload!: WorkflowStepReadOnlyFieldDto;
	formControl!: FormControl;

	get _name(): string {
		return this.payload.name;
	}

	get postBody(): WorkflowStepReadOnlyFieldDto {
		return {
			dynamicPropertyId: this.payload.dynamicPropertyId,
			group: this.payload.group,
			name: this.payload.name,
			selected: this.formControl.value,
			hashCode: 0
		};
	}
}


export class WrapperWorkflowReadOnlyFieldDtoGroup {
	group!: string;
	fields!: WrapperWorkflowReadOnlyFieldDto[];
}


export class WrapperWorkflowStepDto {
	parent!: WrapperWorkflowDto;
	payload!: WorkflowStepDto;
	formGroup!: FormGroup;
	sections!: WrapperWorkflowSectionDto[];
	statuses!: WrapperWorkflowStatusDto[];
	buttons!: WrapperWorkflowButtonDto[];
	readOnlyFields!: WrapperWorkflowReadOnlyFieldDtoGroup[];

	get _label(): string | undefined {
		return this.formGroup.get('label')?.value;
	}
	get _stepOrder(): number | undefined {
		return this.formGroup.get('stepOrder')?.value;
	}
	getRoot(): WrapperWorkflowDto {
		return this.parent;
	}
	get postBody(): WorkflowStepDto {
		const statuses = this.statuses.map(item => item.postBody);
		return {
			id: this.payload.id,
			createdAt: new Date(),
			label: this.formGroup.get('label')!.value,
			description: this.formGroup.get('description')!.value,
			stepOrder: this.formGroup.get('stepOrder')!.value,
			sections: this.sections.map(item => item.postBody),
			statuses: this.statuses.map(item => item.postBody),
			buttons: this.buttons.map(item => item.postBody),
			slaDurationBreach: this.formGroup.get('slaDurationBreach')!.value,
			slaDurationEscalation: this.formGroup.get('slaDurationEscalation')!.value,
			slaDurationWarning: this.formGroup.get('slaDurationWarning')!.value,
			readOnlyFields: this.readOnlyFields.map(item => item.fields).flat().map(item => item.postBody),
			hashCode: 0
		};
	}

	static create(fb: FormBuilder, parent: WrapperWorkflowDto, input: WorkflowStepDto, inputReadOnlyFields: WorkflowStepReadOnlyFieldDto[]): WrapperWorkflowStepDto {
		const out = new WrapperWorkflowStepDto();
		out.parent = parent;
		out.payload = input;
		out.statuses = input.statuses.map(item => WrapperWorkflowStatusDto.create(fb, out, item));
		out.sections = input.sections.map(item => WrapperWorkflowSectionDto.create(fb, out, item));
		out.buttons = input.buttons.map(item => WrapperWorkflowButtonDto.create(fb, out, item));

		const readOnlyFieldWrappers = inputReadOnlyFields.map(item => {
			const wrapper = new WrapperWorkflowReadOnlyFieldDto();
			wrapper.payload = { ...item };
			wrapper.formControl = fb.control(item.selected);
			return wrapper;
		});
		const readOnlyFieldsMap: { [group: string]: WrapperWorkflowReadOnlyFieldDto[] } = {};
		for (const wrapper of readOnlyFieldWrappers) {
			if (!readOnlyFieldsMap[wrapper.payload.group]) {
				readOnlyFieldsMap[wrapper.payload.group] = [];
			}
			readOnlyFieldsMap[wrapper.payload.group].push(wrapper);
		}
		const readOnlyFieldGroups: WrapperWorkflowReadOnlyFieldDtoGroup[] = [];
		for (let key of Object.keys(readOnlyFieldsMap)) {
			readOnlyFieldGroups.push({ group: key, fields: readOnlyFieldsMap[key].sort((i1, i2) => i1.payload.name.localeCompare(i2.payload.name)) });
		}
		out.readOnlyFields = readOnlyFieldGroups.sort((i1, i2) => i1.group.localeCompare(i2.group));


		out.formGroup = fb.group({
			id: input.id,
			description: [input.description, [Validators.required]],
			label: [input.label, [Validators.required]],
			stepOrder: [input.stepOrder, [Validators.required]],
			sections: fb.array(out.sections.map(item => item.formGroup), Validators.required),
			buttons: fb.array(out.buttons.map(item => item.formGroup), Validators.required),
			statuses: fb.array(out.statuses.map(item => item.formGroup), Validators.required),

			// sections: fb.array(sections.map(item => item.formGroup)),
			// buttons: fb.array(buttons.map(item => item.formGroup)),
			// statuses: fb.array(buttons.map(item => item.formGroup)),

			// slaDurationBreach: [input.slaDurationBreach, [Validators.required, Validators.pattern(/^(?i:(?:\d+\s*(?:y|years?|d|days?|w|weeks?|h|hours?|wh|work\s+hours?|wd|work\s+days?|s|seconds?))|\d+\s*M|\d+\s*m(?:inutes?)?)(?:\s*,?\s*(?i:(?:\d+\s*(?:y|years?|d|days?|w|weeks?|h|hours?|wh|work\s+hours?|wd|work\s+days?|s|seconds?))|\d+\s*M|\d+\s*m(?:inutes?)?))*$/)]],
			// slaDurationEscalation: [input.slaDurationEscalation, [Validators.required, Validators.pattern(/^(?i:(?:\d+\s*(?:y|years?|d|days?|w|weeks?|h|hours?|wh|work\s+hours?|wd|work\s+days?|s|seconds?))|\d+\s*M|\d+\s*m(?:inutes?)?)(?:\s*,?\s*(?i:(?:\d+\s*(?:y|years?|d|days?|w|weeks?|h|hours?|wh|work\s+hours?|wd|work\s+days?|s|seconds?))|\d+\s*M|\d+\s*m(?:inutes?)?))*$/)]],
			// slaDurationWarning: [input.slaDurationWarning, [Validators.required, Validators.pattern(/^(?i:(?:\d+\s*(?:y|years?|d|days?|w|weeks?|h|hours?|wh|work\s+hours?|wd|work\s+days?|s|seconds?))|\d+\s*M|\d+\s*m(?:inutes?)?)(?:\s*,?\s*(?i:(?:\d+\s*(?:y|years?|d|days?|w|weeks?|h|hours?|wh|work\s+hours?|wd|work\s+days?|s|seconds?))|\d+\s*M|\d+\s*m(?:inutes?)?))*$/)]],
			slaDurationBreach: [input.slaDurationBreach, [Validators.required, Validators.pattern(/^(\d+\s*(y|year|years|M|month|months|d|day|days|W|week|weeks|h|hour|hours|m|minute|minutes|s|second|seconds|ww|work\s*week|work\s*weeks|wd|work\s*day|work\s*days|wh|work\s*hour|work\s*hours)\s*)+$/i)]],
			slaDurationEscalation: [input.slaDurationEscalation, [Validators.required, Validators.pattern(/^(\d+\s*(y|year|years|M|month|months|d|day|days|W|week|weeks|h|hour|hours|m|minute|minutes|s|second|seconds|ww|work\s*week|work\s*weeks|wd|work\s*day|work\s*days|wh|work\s*hour|work\s*hours)\s*)+$/i)]],
			slaDurationWarning: [input.slaDurationWarning, [Validators.required, Validators.pattern(/^(\d+\s*(y|year|years|M|month|months|d|day|days|W|week|weeks|h|hour|hours|m|minute|minutes|s|second|seconds|ww|work\s*week|work\s*weeks|wd|work\s*day|work\s*days|wh|work\s*hour|work\s*hours)\s*)+$/i)]],

			readOnlyFields: fb.array(readOnlyFieldWrappers.map(item => item.formControl))
		});


		// if (sections.length == 0) {
		// 	WrapperWorkflowSectionDto.add(fb, out);
		// }
		// if (buttons.length == 0) {
		// 	WrapperWorkflowButtonDto.add(fb, out);
		// }
		// if (statuses.length == 0) {
		// 	WrapperWorkflowStatusDto.add(fb, out);
		// }
		return out;
	}
	static add(fb: FormBuilder, parent: WrapperWorkflowDto) {
		const step = WrapperWorkflowStepDto.create(fb, parent, {
			id: WorkflowFormComponent.getNextId(),
			createdAt: new Date(),
			description: '',
			label: 'Next Step',
			stepOrder: parent.steps.length + 1,
			slaDurationWarning: "18h",
			slaDurationBreach: "24h",
			slaDurationEscalation: "36h",
			sections: [],
			buttons: [],
			statuses: [],
			readOnlyFields: [],
			hashCode: 0
		}, parent.payload.availableReadOnlyFields);
		step.parent = parent;
		parent.steps.push(step);
		(parent.form.get('steps')! as FormArray).push(step.formGroup);
	}
	static remove(parent: WrapperWorkflowDto, index: number) {
		parent.steps.splice(index, 1);
		const steps = parent.form.get('steps')! as FormArray;
		steps.removeAt(index);
	}
}


export class WrapperWorkflowStatusDto {
	parent!: WrapperWorkflowStepDto;
	payload!: WorkflowStepStatusDto;
	formGroup!: FormGroup;
	get _label(): string | undefined {
		return this.formGroup.get('label')?.value;
	}
	get _stepOrder(): number | undefined {
		return this.formGroup.get('stepOrder')?.value;
	}
	get _reportAlias(): string | undefined {
		return this.formGroup.get('reportAlias')?.value;
	}
	getRoot(): WrapperWorkflowStepDto {
		return this.parent;
	}
	get postBody(): WorkflowStepStatusDto {
		return {
			id: this.payload.id,
			createdAt: new Date(),
			label: this.formGroup.get('label')!.value,
			reportAlias: this.formGroup.get('reportAlias')!.value,
			hashCode: 0
		};
	}

	static create(fb: FormBuilder, parent: WrapperWorkflowStepDto, input: WorkflowStepStatusDto): WrapperWorkflowStatusDto {
		const out = new WrapperWorkflowStatusDto();
		out.parent = parent;
		out.payload = input;
		out.formGroup = fb.group({
			id: input.id,
			label: [input.label, Validators.required],
			reportAlias: [input.reportAlias]
		});
		return out;
	}
	static add(fb: FormBuilder, parent: WrapperWorkflowStepDto) {
		const status = WrapperWorkflowStatusDto.create(fb, parent, {
			id: WorkflowFormComponent.getNextId(),
			createdAt: new Date(),
			label: '',
			reportAlias: '',
			hashCode: 0
		});
		status.parent = parent;
		parent.statuses.push(status);
		(parent.formGroup.get('statuses')! as FormArray).push(status.formGroup);
		parent.sections.forEach(section => {
			section.elements.forEach(element => {
				element.payload.statusIds.push(status.payload.id);
				const formControl = element.formGroup.get('statusIds')!;
				let statusIds = formControl.value as number[];
				statusIds.push(status.payload.id);
				formControl.setValue(statusIds);
			});

			section.payload.statusIds.push(status.payload.id);
			const formControl = section.formGroup.get('statusIds')!;
			let statusIds = formControl.value as number[];
			statusIds.push(status.payload.id);
			formControl.setValue(statusIds);
		});
	}
	// static remove(parent: WrapperWorkflowStepDto, index: number) {
	// 	parent.statuses.splice(index, 1);
	// 	const steps = parent.formGroup.get('statuses')! as FormArray;
	// 	steps.removeAt(index);
	// }
	static remove(parent: WrapperWorkflowStepDto, index: number) {
		const removedId = parent.statuses[index].payload.id;

		parent.statuses.splice(index, 1);
		const statusesArray = parent.formGroup.get('statuses')! as FormArray;
		statusesArray.removeAt(index);

		// Update sections and elements to remove the deleted status ID

		let defaultStatus: number | undefined = undefined;
		if (parent.statuses.length == 1) {
			defaultStatus = parent.statuses[0].payload.id;
		}

		parent.sections.forEach(section => {
			const sectionStatusIdsControl = section.formGroup.get('statusIds')!;
			let sectionStatusIds = sectionStatusIdsControl.value as number[];
			sectionStatusIds = sectionStatusIds.filter(id => id !== removedId);
			sectionStatusIdsControl.setValue(sectionStatusIds);
			sectionStatusIdsControl.markAsTouched();
			sectionStatusIdsControl.updateValueAndValidity();
			if (defaultStatus && sectionStatusIds.length == 0) {
				sectionStatusIdsControl.setValue([defaultStatus]);
			}
			section.elements.forEach(element => {
				const elementStatusIdsControl = element.formGroup.get('statusIds')!;
				let elementStatusIds = elementStatusIdsControl.value as number[];
				elementStatusIds = elementStatusIds.filter(id => id !== removedId);
				elementStatusIdsControl.setValue(elementStatusIds);
				elementStatusIdsControl.markAsTouched();
				elementStatusIdsControl.updateValueAndValidity();
				if (defaultStatus && elementStatusIds.length == 0) {
					elementStatusIdsControl.setValue([defaultStatus]);
				}
			});
		});
	}
}


export class WrapperWorkflowSectionDto {
	parent!: WrapperWorkflowStepDto;
	payload!: WorkflowStepSectionDto;
	formGroup!: FormGroup;
	elements!: WrapperWorkflowElementDto[];

	get _label(): string | undefined {
		return this.formGroup.get('label')?.value;
	}

	get postBody(): WorkflowStepSectionDto {
		return {
			id: this.payload.id,
			createdAt: new Date(),
			label: this.formGroup.get('label')!.value,
			sectionOrder: this.formGroup.get('sectionOrder')!.value,
			elements: this.elements.map(item => item.postBody),
			statusIds: this.formGroup.get('statusIds')!.value,
			hashCode: 0
		};
	}
	static create(fb: FormBuilder, parent: WrapperWorkflowStepDto, input: WorkflowStepSectionDto): WrapperWorkflowSectionDto {
		const out = new WrapperWorkflowSectionDto();
		out.payload = input;
		out.parent = parent;
		out.elements = input.elements.map(item => WrapperWorkflowElementDto.create(fb, out, item));

		const initialStatusIds = (!input.statusIds || input.statusIds.length === 0) && parent.statuses.length === 1
			? [parent.statuses[0].payload.id]
			: (input.statusIds || []);

		out.formGroup = fb.group({
			id: input.id,
			label: [input.label, Validators.required],
			sectionOrder: [input.sectionOrder, Validators.required],
			elements: fb.array(out.elements.map(item => item.formGroup), Validators.required),
			statusIds: [initialStatusIds, nonEmptyArrayValidator()],
		});

		// if (elements.length == 0) {
		// 	WrapperWorkflowElementDto.add(fb, out);
		// }
		return out;
	}
	static add(fb: FormBuilder, parent: WrapperWorkflowStepDto) {

		const section = WrapperWorkflowSectionDto.create(fb, parent, {
			id: WorkflowFormComponent.getNextId(),
			createdAt: new Date(),
			label: '',
			sectionOrder: parent.sections.length + 1,
			elements: [],
			statusIds: parent.statuses.map(item => item.payload.id),
			hashCode: 0
		});
		parent.sections.push(section);
		(parent.formGroup.get('sections')! as FormArray).push(section.formGroup);
	}
	static remove(parent: WrapperWorkflowStepDto, index: number) {
		parent.sections.splice(index, 1);
		const steps = parent.formGroup.get('sections')! as FormArray;
		steps.removeAt(index);
	}
}


export class WrapperWorkflowElementDto {
	parent!: WrapperWorkflowSectionDto;
	payload!: WorkflowStepSectionElementDto;
	options!: WrapperDynamicFormOptionDto[];
	formGroup!: FormGroup;

	get _label(): string {
		return this.formGroup.get('label')!.value;
	}
	get _description(): string {
		return this.formGroup.get('description')!.value;
	}
	get _dataType(): DynamicValueDataTypeEnum {
		return this.formGroup.get('dataType')!.value;
	}
	get _required(): boolean {
		return this.formGroup.get('required')?.value;
	}
	get _unique(): boolean {
		return this.formGroup.get('unique')?.value;
	}
	get _regex(): string | undefined {
		return this.formGroup.get('regex')?.value;
	}
	get _minValue(): string | undefined {
		return this.formGroup.get('minValue')?.value;
	}
	get _maxValue(): string | undefined {
		return this.formGroup.get('maxValue')?.value;
	}
	get _options(): FormArray | undefined {
		return this.formGroup.get('options')?.value;
	}
	get _icon(): string | undefined {
		return this.formGroup.get('icon')?.value;
	}
	get _flagForDuplicateTicket(): boolean {
		return this.formGroup.get('flagForDuplicateTicket')?.value;
	}

	get postBody(): WorkflowStepSectionElementDto {
		return {
			id: this.payload.id,
			createdAt: new Date(),
			label: this.formGroup.get('label')!.value,
			description: this.formGroup.get('description')!.value,
			dependencyId: this.formGroup.get('dependencyId')?.value,
			orderIndex: this.formGroup.get('orderIndex')!.value,
			icon: this.formGroup.get('icon')!.value,
			dataType: this.formGroup.get('dataType')!.value,
			unique: this.formGroup.get('unique')!.value,
			required: this.formGroup.get('required')!.value,
			dependencyValue: this.formGroup.get('dependencyValue')?.value,
			options: this.options.map(item => item.postBody),
			regex: this.formGroup.get('regex')!.value,
			minValue: this.formGroup.get('minValue')!.value,
			maxValue: this.formGroup.get('maxValue')!.value,
			errorMessageUnique: this.formGroup.get('errorMessageUnique')!.value,
			errorMessageRequired: this.formGroup.get('errorMessageRequired')!.value,
			errorMessageRegex: this.formGroup.get('errorMessageRegex')!.value,
			flagForDuplicateTicket: this.formGroup.get('flagForDuplicateTicket')!.value,
			statusIds: this.formGroup.get('statusIds')!.value,
			hashCode: 0
		};
	}
	static create(fb: FormBuilder, parent: WrapperWorkflowSectionDto, input: WorkflowStepSectionElementDto): WrapperWorkflowElementDto {
		const out = new WrapperWorkflowElementDto();
		out.payload = input;
		out.parent = parent;
		out.options = input.options.map(item => WrapperDynamicFormOptionDto.create(fb, out, item))


		const initialStatusIds = (!input.statusIds || input.statusIds.length === 0) && parent.parent.statuses.length === 1
			? [parent.parent.statuses[0].payload.id]
			: (input.statusIds || []);
		out.formGroup = fb.group({
			id: [input.id],
			label: [input.label ?? '', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
			description: [input.description ?? '', [Validators.required, Validators.minLength(1), Validators.maxLength(255)]],
			dataType: [input.dataType ?? 'Text', [Validators.required]],
			icon: [input.icon],
			required: [input.required ?? true, [Validators.required]],
			errorMessageRequired: [input.errorMessageRequired ?? '', [Validators.maxLength(255)]],
			unique: [input.unique ?? false, [Validators.required]],
			errorMessageUnique: [input.errorMessageUnique ?? '', [Validators.maxLength(255)]],
			regex: [input.regex ?? ''],
			errorMessageRegex: [input.errorMessageRegex ?? ''],
			minValue: [input.minValue ?? '', [Validators.pattern('^[0-9]+$')]],
			errorMessageMinValue: [input.errorMessageMinValue ?? ''],
			maxValue: [input.maxValue ?? '', [Validators.pattern('^[0-9]+$')]],
			errorMessageMaxValue: [input.errorMessageMaxValue ?? ''],
			orderIndex: [input.orderIndex ?? '', [Validators.required]],
			statusIds: [initialStatusIds, nonEmptyArrayValidator()],
			flagForDuplicateTicket: [input.flagForDuplicateTicket],
			dependencyId: [input.dependencyId]
		});

		if (input.dataType == 'DropDownSingle') {
			out.formGroup.addControl('options', fb.array(out.options.map(item => item.formGroup), Validators.required));
		}
		out.formGroup.get('dataType')!.valueChanges.subscribe(value => {
			if (value === 'DropDownSingle') {
				if (!out.formGroup.contains('options')) {
					out.formGroup.addControl('options', fb.array(out.options.map(item => item.formGroup), Validators.required));
				}
			} else {
				if (out.formGroup.contains('options')) {
					out.formGroup.removeControl('options');
				}
			}
		});

		if (input.dependencyId) {
			out.formGroup.addControl('dependencyValue', fb.control(input.dependencyValue, [Validators.required]));
		}
		out.formGroup.get('dependencyId')!.valueChanges.subscribe(dependencyId => {
			if (dependencyId) {
				if (!out.formGroup.contains('dependencyValue')) {
					out.formGroup.addControl('dependencyValue', fb.control(input.dependencyValue, [Validators.required]));
				}
			} else {
				if (out.formGroup.contains('dependencyValue')) {
					out.formGroup.removeControl('dependencyValue');
				}
			}
		});

		return out;
	}
	static add(fb: FormBuilder, parent: WrapperWorkflowSectionDto): WrapperWorkflowElementDto {
		const element = WrapperWorkflowElementDto.create(fb, parent, {
			id: WorkflowFormComponent.getNextId(),
			createdAt: new Date(),
			label: '',
			description: '',
			dependencyId: undefined,
			orderIndex: parent.elements.length + 1,
			icon: '',
			dataType: 'Text',
			unique: false,
			required: true,
			dependencyValue: undefined,
			options: [],
			regex: undefined,
			minValue: undefined,
			maxValue: undefined,
			errorMessageUnique: undefined,
			errorMessageRequired: undefined,
			errorMessageRegex: undefined,
			flagForDuplicateTicket: false,
			statusIds: parent.parent.statuses.map(item => item.payload.id),
			hashCode: 0
		});
		parent.elements.push(element);
		(parent.formGroup.get('elements')! as FormArray).push(element.formGroup);
		return element;
	}
	static remove(parent: WrapperWorkflowSectionDto, index: number) {
		parent.elements.splice(index, 1);
		const steps = parent.formGroup.get('elements')! as FormArray;
		steps.removeAt(index);
	}
}


export class WrapperWorkflowButtonDto {
	parent!: WrapperWorkflowStepDto;
	payload!: WorkflowStepButtonDto;
	formGroup!: FormGroup;
	actions!: WrapperWorkflowActionDto[];

	get _label(): string {
		return this.formGroup.get('label')!.value;
	}
	get _buttonStyle(): string {
		return this.formGroup.get('buttonStyle')!.value ?? 'Default';
	}
	get _buttonType(): NzButtonType {
		return (this.formGroup.get('buttonType')!.value ?? 'Default' as string).toLowerCase();
	}
	get _alignment(): ButtonAlignmentEnum {
		return this.formGroup.get('alignment')!.value ?? 'Left';
	}

	getStep() {
		return this.parent;
	}
	getRoot(): WrapperWorkflowDto {
		return this.parent.getRoot();
	}
	get postBody(): WorkflowStepButtonDto {
		return {
			id: this.payload.id,
			createdAt: new Date(),
			label: this.formGroup.get('label')!.value,
			statusId: this.formGroup.get('statusId')!.value,
			dependencyId: this.formGroup.get('dependencyId')!.value,
			dependencyValue: this.formGroup.get('dependencyValue')!.value,
			orderIndex: this.formGroup.get('orderIndex')!.value,
			icon: this.formGroup.get('icon')!.value,
			buttonStyle: this.formGroup.get('buttonStyle')!.value,
			buttonType: this.formGroup.get('buttonType')!.value,
			requiredValidForm: this.formGroup.get('requiredValidForm')!.value,
			alignment: this.formGroup.get('alignment')!.value,
			actions: this.actions.map(item => item.postBody),
			hashCode: 0
		};
	}
	static create(fb: FormBuilder, parent: WrapperWorkflowStepDto, input: WorkflowStepButtonDto): WrapperWorkflowButtonDto {
		const out = new WrapperWorkflowButtonDto();
		out.payload = input;
		out.parent = parent;
		out.actions = input.actions.sort((i1, i2) => i1.orderIndex - i2.orderIndex).map(item => WrapperWorkflowActionDto.create(fb, out, item));

		out.formGroup = fb.group({
			id: [input.id, [Validators.required]],
			label: [input.label, [Validators.required]],
			statusId: [input.statusId, [Validators.required]],
			dependencyId: [input.dependencyId],
			dependencyValue: [input.dependencyValue],
			orderIndex: [input.orderIndex, [Validators.required]],
			buttonStyle: [input.buttonStyle, [Validators.required]],
			buttonType: [input.buttonType, [Validators.required]],
			alignment: [input.alignment, [Validators.required]],
			icon: [input.icon],
			requiredValidForm: [input.requiredValidForm, [Validators.required]],
			actions: fb.array(out.actions.map(item => item.formGroup), Validators.required),
			// actions: fb.array(actions.map(item => item.formGroup))
		});

		// if (actions.length == 0) {
		// 	WrapperWorkflowActionDto.add(fb, out);
		// }
		return out;
	}
	static add(fb: FormBuilder, parent: WrapperWorkflowStepDto, status: WrapperWorkflowStatusDto): WrapperWorkflowButtonDto {
		const button = WrapperWorkflowButtonDto.create(fb, parent, {
			id: WorkflowFormComponent.getNextId(),
			createdAt: new Date(),
			label: 'button',
			statusId: status.payload.id,
			orderIndex: parent.buttons.length + 1,
			icon: '',
			requiredValidForm: true,
			buttonStyle: 'Default',
			buttonType: 'Default',
			alignment: 'Left',
			actions: [],
			hashCode: 0
		});
		parent.buttons.push(button);
		(parent.formGroup.get('buttons')! as FormArray).push(button.formGroup);
		return button;
	}
	static remove(parent: WrapperWorkflowStepDto, index: number) {
		parent.buttons.splice(index, 1);
		const steps = parent.formGroup.get('buttons')! as FormArray;
		steps.removeAt(index);
	}
}


export class WrapperWorkflowActionDto {
	parent!: WrapperWorkflowButtonDto;
	payload!: WorkflowStepButtonActionDto;
	formGroup!: FormGroup;

	get _actionType(): WorkflowActionTypeEnum {
		return this.formGroup.get('actionType')!.value;
	}
	get _orderIndex(): number {
		return this.formGroup.get('orderIndex')!.value;
	}

	get postBody(): WorkflowStepButtonActionDto {
		return {
			id: this.payload.id,
			createdAt: new Date(),
			actionType: this.formGroup.get('actionType')!.value,
			orderIndex: this.formGroup.get('orderIndex')!.value,
			name: this.formGroup.get('name')?.value ?? '',
			emailFrom: this.formGroup.get('emailFrom')?.value ?? '',
			emailTo: this.formGroup.get('emailTo')?.value ?? '',
			emailCc: this.formGroup.get('emailCc')?.value ?? '',
			emailBcc: this.formGroup.get('emailBcc')?.value ?? '',
			emailSubject: this.formGroup.get('emailSubject')?.value ?? '',
			emailBody: this.formGroup.get('emailBody')?.value ?? '',
			attachments: this.formGroup.get('attachments')?.value ?? [],

			smsRecipients: this.formGroup.get('smsRecipients')?.value ?? '',
			smsMessage: this.formGroup.get('smsMessage')?.value ?? '',
			movementWorkflowStepId: undefined as any,
			movementWorkflowStepStatusId: this.formGroup.get('movementWorkflowStepStatusId')?.value ?? '',

			additionalCommentRequired: this.formGroup.get('additionalCommentRequired')?.value,
			hashCode: 0
		};
	}
	static create(fb: FormBuilder, parent: WrapperWorkflowButtonDto, input: WorkflowStepButtonActionDto): WrapperWorkflowActionDto {
		const out = new WrapperWorkflowActionDto();
		out.payload = input;
		out.parent = parent;
		out.formGroup = fb.group({
			id: [input.id],
			actionType: [input.actionType, [Validators.required]],
			orderIndex: [input.orderIndex, [Validators.required]]
		});
		if (input.actionType == 'AdditionalCommunication' || input.actionType == 'RequiredCommunication') {
			out.formGroup.addControl('name', new FormControl(input.name ?? '', [Validators.required]));
			out.formGroup.addControl('emailFrom', new FormControl(input.emailFrom ?? '', [Validators.required]));
			out.formGroup.addControl('emailTo', new FormControl(input.emailTo ?? ''));
			out.formGroup.addControl('emailCc', new FormControl(input.emailCc ?? ''));
			out.formGroup.addControl('emailBcc', new FormControl(input.emailBcc ?? ''));
			out.formGroup.addControl('emailSubject', new FormControl(input.emailSubject ?? '', [Validators.required]));
			out.formGroup.addControl('emailBody', new FormControl(input.emailBody ?? '', [Validators.required]));
			out.formGroup.addControl('attachments', fb.array(input.attachments ?? []));
		} else {
			out.formGroup.removeControl('name');
			out.formGroup.removeControl('emailFrom');
			out.formGroup.removeControl('emailTo');
			out.formGroup.removeControl('emailCc');
			out.formGroup.removeControl('emailBcc');
			out.formGroup.removeControl('emailSubject');
			out.formGroup.removeControl('emailBody');
			out.formGroup.removeControl('attachments');
		}

		if (input.actionType == 'Movement') {
			out.formGroup.addControl('movementWorkflowStepStatusId', new FormControl(input.movementWorkflowStepStatusId, Validators.required));
		} else {
			out.formGroup.removeControl('movementWorkflowStepStatusId');
		}

		if (input.actionType == 'AdditionalComment') {
			out.formGroup.addControl('additionalCommentRequired', new FormControl(input.additionalCommentRequired));
		} else {
			out.formGroup.removeControl('additionalCommentRequired');
		}

		out.formGroup.get('actionType')!.valueChanges.subscribe(value => {
			console.log("Action Type changed.  ", value, input.actionType);

			if (value != 'RequiredCommunication' && value != 'AdditionalCommunication') {
				out.formGroup.removeControl('name');
				out.formGroup.removeControl('emailFrom');
				out.formGroup.removeControl('emailTo');
				out.formGroup.removeControl('emailCc');
				out.formGroup.removeControl('emailBcc');
				out.formGroup.removeControl('emailSubject');
				out.formGroup.removeControl('emailBody');
				out.formGroup.removeControl('attachments');
			}
			if (value != 'Movement') {
				out.formGroup.removeControl('movementWorkflowStepStatusId');
			}
			if (value != 'AdditionalComment') {
				out.formGroup.removeControl('additionalCommentRequired');
			}




			if (value == 'RequiredCommunication' || value == 'AdditionalCommunication') {
				out.formGroup.addControl('name', new FormControl(input.name ?? '', [Validators.required]));
				out.formGroup.addControl('emailFrom', new FormControl(input.emailFrom ?? '', [Validators.required]));
				out.formGroup.addControl('emailTo', new FormControl(input.emailTo ?? ''));
				out.formGroup.addControl('emailCc', new FormControl(input.emailCc ?? ''));
				out.formGroup.addControl('emailBcc', new FormControl(input.emailBcc ?? ''));
				out.formGroup.addControl('emailSubject', new FormControl(input.emailSubject ?? '', [Validators.required]));
				out.formGroup.addControl('emailBody', new FormControl(input.emailBody ?? '', [Validators.required]));
				out.formGroup.addControl('attachments', fb.array(input.attachments ?? []));
			}

			if (value == 'Movement') {
				out.formGroup.addControl('movementWorkflowStepStatusId', new FormControl(input.movementWorkflowStepStatusId, Validators.required));
			}
			if (value == 'AdditionalComment') {
				out.formGroup.addControl('additionalCommentRequired', new FormControl(input.additionalCommentRequired));
			}
		});

		return out;
	}

	static add(fb: FormBuilder, parent: WrapperWorkflowButtonDto): WrapperWorkflowActionDto {
		const action = WrapperWorkflowActionDto.create(fb, parent, {
			id: WorkflowFormComponent.getNextId(),
			createdAt: new Date(),
			actionType: 'Movement',
			orderIndex: parent.actions.length + 1,
			hashCode: 0
		});
		parent.actions.push(action);
		(parent.formGroup.get('actions')! as FormArray).push(action.formGroup);
		return action;
	}
	static remove(parent: WrapperWorkflowButtonDto, index: number) {
		parent.actions.splice(index, 1);
		const steps = parent.formGroup.get('actions')! as FormArray;
		steps.removeAt(index);
	}
}


export class WrapperDynamicFormOptionDto {
	parent!: WrapperWorkflowElementDto;
	payload!: DynamicFormOptionDto;
	formGroup!: FormGroup;

	get _label(): string | undefined {
		return this.formGroup.get('label')?.value;
	}
	get _value(): string | undefined {
		return this.formGroup.get('value')?.value;
	}
	get _orderIndex(): number | undefined {
		return this.formGroup.get('orderIndex')?.value;
	}
	get postBody(): DynamicFormOptionDto {
		return {
			value: this.formGroup.get('value')!.value,
			label: this.formGroup.get('label')?.value,
			orderIndex: this.formGroup.get('orderIndex')!.value,
			hashCode: 0
		};
	}
	static create(fb: FormBuilder, parent: WrapperWorkflowElementDto, input: DynamicFormOptionDto): WrapperDynamicFormOptionDto {
		const out = new WrapperDynamicFormOptionDto();
		out.payload = input;
		out.parent = parent;
		out.formGroup = fb.group({
			value: [input.value, [Validators.required]],
			label: [input.label, [Validators.required]],
			orderIndex: [input.orderIndex, [Validators.required]]
		});
		return out;
	}

	static add(fb: FormBuilder, parent: WrapperWorkflowElementDto): WrapperDynamicFormOptionDto {
		const option = WrapperDynamicFormOptionDto.create(fb, parent, {
			value: '',
			label: '',
			orderIndex: parent.options.length + 1,
			hashCode: 0
		});
		parent.options.push(option);

		const formArray = parent.formGroup.get('options') as FormArray;
		if (formArray) {
			formArray.push(option.formGroup);
		}
		return option;
	}
	static remove(parent: WrapperWorkflowElementDto, index: number) {
		parent.options.splice(index, 1);
		const formArray = parent.formGroup.get('options') as FormArray;
		if (formArray) {
			formArray.removeAt(index);
		}
	}
}




export function doGetInvalidControlsList(group: FormGroup | FormArray, path: string = ''): { field: string; errors: string }[] {
	let invalid: { field: string; errors: string }[] = [];

	// Check the current group/array itself
	if (group.errors) {
		const errorKeys = Object.keys(group.errors).join(', ');
		invalid.push({ field: path || '(root)', errors: errorKeys });
	}

	// Then iterate its children
	Object.keys(group.controls).forEach(key => {
		const controlPath = path ? `${path}.${key}` : key;
		const control = group.get(key)!;

		if (control instanceof FormControl) {
			if (control.invalid) {
				const errorKeys = Object.keys(control.errors || {}).join(', ');
				invalid.push({ field: controlPath, errors: errorKeys });
			}
		} else if (control instanceof FormGroup || control instanceof FormArray) {
			invalid = invalid.concat(doGetInvalidControlsList(control, controlPath));
		}
	});

	return invalid;
}

export function doTouchAllFields(group: FormGroup | FormArray) {
	Object.values(group.controls).forEach(control => {
		if (control instanceof FormControl) {
			control.markAsDirty({ onlySelf: true });
			control.updateValueAndValidity({ onlySelf: true });
		} else if (control instanceof FormGroup || control instanceof FormArray) {
			doTouchAllFields(control);
		}
	});
}
